import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import Stub from "@/pages/Stub";
import { usePageMeta } from "@/lib/usePageMeta";

const CAL_URL = "https://cal.com/saber-bellaaj-5ogauj/rdv-weyb.fr";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const formatDateFr = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
    } catch {
        return iso;
    }
};

export default function ArticleDetail() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [notFound, setNotFound] = useState(false);
    usePageMeta(
        article ? article.seo_title || article.title : { fr: "Analyses & ressources export — WEYB", en: "Export insights & resources — WEYB" },
        article ? article.meta_description || article.excerpt : undefined
    );

    useEffect(() => {
        axios
            .get(`${API}/articles/${slug}`)
            .then((res) => setArticle(res.data))
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) return <Stub title="Article introuvable" />;
    if (!article) return <div className="min-h-screen bg-white" data-testid="article-loading" />;

    const articleLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.meta_description || article.excerpt,
        image: article.image,
        datePublished: article.date,
        author: { "@type": "Person", name: article.author, jobTitle: "Fondateur & CEO de WEYB" },
        publisher: { "@type": "Organization", name: "WEYB" },
    };

    return (
        <article data-testid="article-page" className="bg-white pt-32 md:pt-44 pb-24">
            <script type="application/ld+json">{JSON.stringify(articleLd)}</script>
            <script type="application/ld+json">{JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Accueil", item: "https://weyb.fr/" },
                    { "@type": "ListItem", position: 2, name: "Analyses", item: "https://weyb.fr/analyses" },
                    { "@type": "ListItem", position: 3, name: article.title },
                ],
            })}</script>

            <div className="max-w-3xl mx-auto px-6">
                <Reveal>
                    <Link
                        to="/analyses"
                        data-testid="article-back-link"
                        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-gray-400 hover:text-weyb-yellow-dark transition-colors"
                    >
                        <ArrowLeft size={14} />
                        Analyses & Ressources
                    </Link>
                </Reveal>

                <Reveal delay={0.06}>
                    <p className="mt-10 text-[11px] uppercase tracking-[0.2em] font-semibold text-weyb-yellow-dark">
                        {article.category}
                    </p>
                </Reveal>
                <Reveal delay={0.1}>
                    <h1 className="mt-4 font-display text-3xl md:text-5xl font-light tracking-tight text-weyb-ink leading-[1.1]">
                        {article.title}
                    </h1>
                </Reveal>
                <Reveal delay={0.16}>
                    <div className="mt-6 flex items-center gap-4 text-sm text-gray-500 border-b border-gray-100 pb-8">
                        <span className="font-semibold text-weyb-ink" data-testid="article-author">{article.author}</span>
                        <span className="h-3 w-px bg-gray-200" aria-hidden="true" />
                        <time dateTime={article.date} data-testid="article-date">{formatDateFr(article.date)}</time>
                    </div>
                </Reveal>

                {article.image && (
                    <Reveal delay={0.2}>
                        <div className="mt-10 overflow-hidden rounded-sm">
                            <img src={article.image} alt={article.title} className="w-full aspect-[16/9] object-cover" data-testid="article-image" />
                        </div>
                    </Reveal>
                )}

                <Reveal delay={0.24}>
                    <div className="mt-10 space-y-6" data-testid="article-content">
                        {article.content.split("\n\n").map((p, i) => (
                            <p key={i} className={`leading-relaxed ${i === 0 ? "text-lg md:text-xl font-light text-weyb-ink/85" : "text-base text-gray-700"}`}>
                                {p}
                            </p>
                        ))}
                    </div>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="mt-16 bg-weyb-ink text-white rounded-sm p-8 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div>
                            <h2 className="font-display text-xl md:text-2xl font-light tracking-tight">
                                Votre projet international mérite un vrai diagnostic.
                            </h2>
                            <p className="mt-2 text-sm text-white/60">
                                Un échange de 30 minutes avec Saber Bellaaj, sans engagement.
                            </p>
                        </div>
                        <a
                            href={CAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="article-cta-booking"
                            className="inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-6 py-3.5 rounded-sm text-sm font-semibold transition-colors shrink-0"
                        >
                            Réserver un échange
                            <ArrowUpRight size={15} />
                        </a>
                    </div>
                </Reveal>
            </div>
        </article>
    );
}
